import {
  Box,
  VStack,
  Heading,
  Button,
  Icon,
  Flex,
} from "@chakra-ui/react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useLang } from "../../Hooks";
import {
  MdDashboard,
  MdReceiptLong,
  MdFolderOpen,
  MdMessage,
  MdLogout,
  MdCheckCircleOutline,
  MdAssessment, 
} from "react-icons/md";
import ClientLogo from "../ClientLogo";
import { useBranding } from "../../Contexts/BrandingContext";

const NavItem = ({ 
  icon, 
  children, 
  to, 
  isCollapsed, 
  onClick,
  activeColor,
}) => {
  const location = useLocation();
  const isActive =
    to !== "/" && (location.pathname === to || (to !== "/dashboard" && location.pathname.startsWith(to)));

  const activeBg = isActive ? activeColor || "var(--sidebar-item-active-bg)" : "transparent";

  const handleClick = (e) => {
    e.stopPropagation(); // نوقف انتشار الحدث عشان منعملش توجل مرتين
    if (onClick) {
      onClick(e);
    }
  };

  return (
    <Button
      as={to ? RouterLink : "button"}
      to={to}
      onClick={handleClick}
      variant="ghost"
      justifyContent={isCollapsed ? "center" : "flex-start"}
      pl={isCollapsed ? 0 : 4}
      w="full"
      leftIcon={
        !isCollapsed && icon ? <Icon as={icon} boxSize={5} mr={3} /> : undefined
      }
      rightIcon={
        isCollapsed && icon ? <Icon as={icon} boxSize={6} /> : undefined
      }
      borderRadius="md"
      title={isCollapsed ? children : undefined}
      fontSize="sm"
      py={6}
      sx={{
        bg: activeBg,
        color: isActive
          ? "var(--sidebar-item-active-text)"
          : "var(--sidebar-text-color)",
        _hover: {
          bg: "var(--sidebar-item-hover-bg)",
          color: "var(--sidebar-text-hover-color)",
        },
      }}
    >
      {!isCollapsed && children}
    </Button>
  );
};

const Sidebar = ({
  isCollapsed,
  onToggleCollapse,
  onLogout,
  onCloseDrawer,
}) => {
  const { texts } = useLang();
  const sidebarTexts = texts?.sidebar ?? {};
  const { Name } = useBranding();

  const currentHostname = typeof window !== 'undefined' ? window.location.hostname : '';
  
  const allNavItems = [
    { icon: MdDashboard, labelKey: "dashboard", to: "/dashboard" },
    {
      icon: MdReceiptLong,
      labelKey: "billingInvoices",
      to: "/dashboard/billing",
    },
    { icon: MdFolderOpen, labelKey: "documents", to: "/dashboard/documents" },
    { icon: MdMessage, labelKey: "messagesSupport", to: "/dashboard/chat" },
    {
      icon: MdCheckCircleOutline,
      labelKey: "includedServices",
      to: "/dashboard/includedServices",
    },
    {
      icon: MdAssessment,
      labelKey: "reports",
      to: "/dashboard/services", 
    },
    {
      icon: MdAssessment,
      labelKey: "MaksabReports",
      to: "/dashboard/MaksabReports", 
    },
    {
      icon: MdAssessment,
      labelKey: "ManualReports",
      to: "/dashboard/ManualReports", 
    },
  ];

  const getActiveColorForItem = () => {
    if (['client.tadweeen.com', 'clients.tadweeen.com'].includes(currentHostname)) {
      return "#F9A825"
    }
    return undefined;
  };

  const navItems = allNavItems.filter(item => {
    const alwaysVisible = [
      "/dashboard",
      "/dashboard/billing",
      "/dashboard/documents",
      "/dashboard/chat",
      "/dashboard/includedServices",
      "/"
    ];
    
    if (alwaysVisible.includes(item.to)) {
      return true;
    }
    
    if (item.to === "/dashboard/services") {
      return ['client.tadweeen.com', 'clients.tadweeen.com'].includes(currentHostname) || !['client.maksab-ksa.com','clients.maksab-ksa.com'].includes(currentHostname);
    }
    
    if (item.to === "/dashboard/MaksabReports") {
       return !['client.tadweeen.com', 'clients.tadweeen.com'].includes(currentHostname) || ['client.maksab-ksa.com','clients.maksab-ksa.com'].includes(currentHostname);
    }
    
    if (item.to === "/dashboard/ManualReports") {
      return true; 
    }
    
    return false;
  });

  // Handle clicking anywhere on the sidebar
  const handleSidebarClick = (e) => {
    // Prevent toggling if clicking on the logout button
    if (e.target.closest('.logout-button')) {
      return;
    }
    onToggleCollapse();
  };

  return (
    <>
      <Box
        as="nav"
        h="full"
        display="flex"
        flexDirection="column"
        onClick={handleSidebarClick}
        cursor="pointer"
        sx={{
          bg: 'var(--sidebar-bg)',
          color: 'var(--sidebar-heading-color)',
          transition: 'all 0.2s',
        }}
      >
        <Flex
          align="center"
          justify={isCollapsed ? "center" : "space-between"}
          p={isCollapsed ? 2 : 4}
          h="70px"
          borderBottomWidth="1px"
          flexShrink={0}
          sx={{ borderColor: 'var(--sidebar-border-color)' }}
          // شيلنا الـ stopPropagation من هنا عشان اللوجو والعنوان يشتغل معاهم التوجل
        >
          {!isCollapsed ? (
            <Flex 
              align="center" 
              width="100%"
              height="100%"
              gap={3}
            >
              <Box 
                width="45%" 
                height="100%"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <ClientLogo 
                  containerProps={{ 
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    maxWidth: "100%",
                  }} 
                />
              </Box>
              
              <Box 
                width="45%" 
                height="100%"
                display="flex"
                alignItems="center"
              >
                <Heading 
                  size="md" 
                  fontSize="17px" 
                  fontWeight="bold"
                  noOfLines={2}
                  lineHeight="1.4"
                  letterSpacing="0.5px"
                >
                  {Name || "Exista Business Solutions LLC"}
                </Heading>
              </Box>
            </Flex>
          ) : (
            <Box 
              width="100%" 
              height="100%"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <ClientLogo 
                containerProps={{ 
                  width: "80px", 
                  height: "80px",
                  objectFit: "contain",
                }} 
              />
            </Box>
          )}
        </Flex>

        <VStack
          spacing={1}
          align="stretch"
          p={isCollapsed ? 1 : 2}
          mt={2}
          flexGrow={1}
          overflowY="auto"
        >
          {navItems.map((item) => (
            <NavItem
              key={`${item.labelKey}-${item.to}`}
              icon={item.icon}
              to={item.to}
              isCollapsed={isCollapsed}
              onCloseDrawer={onCloseDrawer}
              onClick={item.onClick}
              activeColor={getActiveColorForItem(item)}
            >
              {sidebarTexts[item.labelKey] ?? item.labelKey}
            </NavItem>
          ))}
        </VStack>

        <Box
          p={2}
          mt="auto"
          borderTopWidth="1px"
          flexShrink={0}
          sx={{ borderColor: 'var(--sidebar-border-color)' }}
          onClick={(e) => e.stopPropagation()} // بسيبها هنا عشان اللوجاوت ميعملش توجل
        >
          <Button
            variant="ghost"
            w="full"
            onClick={(e) => {
              e.stopPropagation();
              onLogout();
            }}
            justifyContent={isCollapsed ? "center" : "flex-start"}
            className="logout-button"
            sx={{
              color: 'var(--sidebar-logout-text)',
              _hover: { 
                bg: 'var(--sidebar-logout-hover-bg)', 
                color: 'var(--sidebar-logout-hover-text)' 
              },
            }}
          >
            {isCollapsed ? (
              <Icon as={MdLogout} boxSize={6} />
            ) : (
              sidebarTexts.signOut ?? "Sign Out"
            )}
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default Sidebar;
